/**
 * couponState — décision PURE de l'état coupon (réduction de prix AFFICHÉE).
 *
 * Extrait du refactor B2 (§9/§69). Module SANS dépendance (ni React ni service) pour rester
 * characterization-testable en env node (§71) et réutilisable par `useCouponField`.
 *
 * ⚠️ Coupon = remise AFFICHÉE uniquement ; prix réel recalculé SERVER-SIDE (note registre `tileTransfer`).
 * Aucune capture de paiement, aucun verrou (§13/§46 hors scope).
 */

/**
 * computeCouponState — décide l'état coupon suivant à partir de la réponse serveur.
 *
 * Branches (reproduisent EXACTEMENT les 5 copies — cf. BRANCH-DIFF TRACK B2) :
 *   - empty   : `res === '__empty__'` (champ vide / null) → reset, aucun coupon, aucun appel réseau.
 *   - valid   : `res` objet `{ id, discount }` → coupon valide, prix remisé.
 *   - overdue : `res === true` → coupon expiré.
 *   - invalid : `res` falsy → coupon inconnu.
 *
 * Sentinelle de prix hors cas VALID :
 *   - `resetSentinel='base'` → `price = amount` (Famille A : TileDetails/TileGroupDetails/ItemDetails/ParcelDetails).
 *   - `resetSentinel='null'` → `price = null`   (Famille B : UECheckoutModal).
 *
 * @param {Object} p
 * @param {*}      p.res            réponse `CouponService.fetchCoupon`, ou `'__empty__'` pour le cas vide
 * @param {number} p.amount        prix de base (number) pour le calcul de la remise
 * @param {'base'|'null'} p.resetSentinel
 * @returns {{ error:boolean, set:boolean, overdue:boolean, id:string, price:(number|null), discount:number }}
 *
 * `discount` = pourcentage de remise du coupon résolu (0 hors cas VALID). Surfacé pour
 * permettre un recalcul AFFICHÉ par-item (ex. panier : remise réservée aux items éligibles).
 * Reste display-only ; le prix réel est recalculé SERVER-SIDE.
 */
export function computeCouponState({ res, amount, resetSentinel }) {
  const resetPrice = resetSentinel === 'null' ? null : amount;

  // Champ vide (sentinelle interne) — reset complet.
  if (res === '__empty__') {
    return { error: false, set: false, overdue: false, id: '', price: resetPrice, discount: 0 };
  }
  // Coupon valide : objet { id, discount }.
  // P2-7 — remise bornée [0..100] AVANT calcul (miroir du clamp serveur,
  // cartValidationService.applyNftCoupon) : jamais de prix négatif ni de remise > 100 %.
  if (res && res !== true) {
    const pct = Math.min(100, Math.max(0, Number(res.discount) || 0));
    return {
      error: false,
      set: true,
      overdue: false,
      id: res.id,
      price: parseInt(amount * (1 - pct / 100)),
      discount: pct,
    };
  }
  // Coupon expiré : res === true.
  if (res === true) {
    return { error: true, set: false, overdue: true, id: '', price: resetPrice, discount: 0 };
  }
  // Coupon inconnu : res falsy.
  return { error: true, set: false, overdue: false, id: '', price: resetPrice, discount: 0 };
}
